/**
 * @file Invoice.config.ts
 * @description Configuration constants and mappings for Invoice forms
 * @module Invoices/Invoice
 */

import { useNavigate } from "react-router-dom";
import { SelectOption } from "components/dropdown/Dropdown";
import { ActionLinkConfig } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import { ACTION_KEY, ACTION_SELECTED_ID_KEY, APP_ROUTES, FILTER_KEY, endpoints } from "config";
import { ConditionalFields } from "components/Entity/Entity";

/**
 * Conditional field visibility rules for Supplier Feedback fields (update/edit mode).
 *
 * Controls visibility of NCR and Compliment fields based on the selected
 * SupplierFeedbackTypesID dropdown value (resolved via datasource internalCode).
 *
 * Business Rules:
 * - **EXCEEDED_EXPECTATIONS** → show Compliment, hide NCR Category + NCR Description
 * - **DID_NOT_MEET_EXPECTATIONS** → show NCR Category + NCR Description, hide Compliment
 * - **Any other value / null** → hide all 3 dependent fields
 *
 * The `datasourceField` property tells Entity's `filterGroupedMetaData` to look up
 * the `listSupplierFeedbackTypes` array in `formik.values`, find the entry whose
 * `.value` matches the current `supplierFeedbackTypesID`, and compare its
 * `.internalCode` against `dependentValues`.
 *
 * @see supplierFeedbackViewConditionalFields — view-mode equivalent
 */
export const supplierFeedbackConditionalFields: ConditionalFields = {
  Compliment: {
    dependentField: "SupplierFeedbackTypesID",
    dependentValues: ["EXCEEDED_EXPECTATIONS"],
    datasourceField: "listSupplierFeedbackTypes",
  },
  NcrCategoryTypesID: {
    dependentField: "SupplierFeedbackTypesID",
    dependentValues: ["DID_NOT_MEET_EXPECTATIONS"],
    datasourceField: "listSupplierFeedbackTypes",
  },
  NcrCategoryName: {
    dependentField: "SupplierFeedbackTypesID",
    dependentValues: ["DID_NOT_MEET_EXPECTATIONS"],
    datasourceField: "listSupplierFeedbackTypes",
  },
  NcrDescription: {
    dependentField: "SupplierFeedbackTypesID",
    dependentValues: ["DID_NOT_MEET_EXPECTATIONS"],
    datasourceField: "listSupplierFeedbackTypes",
  },
};

/**
 * Conditional field visibility rules for Supplier Feedback fields (view mode).
 *
 * In view mode the API returns `SupplierFeedbackName` (string) instead of
 * `SupplierFeedbackTypesID` (number) and does **not** include the
 * `listSupplierFeedbackTypes` datasource array. This means the update-mode
 * config (`supplierFeedbackConditionalFields`) cannot resolve visibility.
 *
 * Strategy: show each field only when it has a non-null string value.
 * Uses self-referential `dependentField` + `dependentType: "string"` so that
 * Entity's `filterGroupedMetaData` evaluates `typeof fieldValue === "string"`:
 *   - Non-null string → `true`  → field is **shown**
 *   - `null`          → `false` → field is **hidden**
 *
 * View mode uses NcrCategoryName (StringReadOnly) instead of NcrCategoryTypesID
 * (dropdown), so the config maps the view-mode field names.
 *
 * @see supplierFeedbackConditionalFields — update/edit-mode equivalent
 */
export const supplierFeedbackViewConditionalFields: ConditionalFields = {
  Compliment: {
    dependentField: "Compliment",
    dependentValues: null,
    dependentType: "string",
  },
  NcrCategoryName: {
    dependentField: "NcrCategoryName",
    dependentValues: null,
    dependentType: "string",
  },
  NcrDescription: {
    dependentField: "NcrDescription",
    dependentValues: null,
    dependentType: "string",
  },
};

/**
 * Accepted file types for invoice uploads
 * Supports: JPG, JPEG, PNG, and PDF files
 */
export const INVOICE_ACCEPTED_FILE_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "application/pdf",
];

/**
 * Maximum file size for invoice uploads
 * Set to 500MB (500000000 bytes)
 */
export const MAX_FILE_SIZE = 500000000; // 500MB

/**
 * Endpoint configuration for search fields in invoice forms
 * Maps field names to their respective search endpoints
 */
export const endpointConfig = {
  editableByRoleIds: endpoints.gateways.search.role,
  suppliersID: endpoints.invoices.search.suppliers,
  projectManagersID: endpoints.invoices.search.projectManagers,
  programsID: endpoints.invoices.search.programs,
  programmesID: endpoints.invoices.search.programs,
  assignedToID: endpoints.invoices.search.assignedUsers,
};

/**
 * Configuration for "View" action links in invoice forms
 * Defines navigation routes and permissions for viewing related entities
 */
export const linkConfig: ActionLinkConfig = {
  clientID: {
    text: "View client",
    permissionKey: "view",
    location: APP_ROUTES.companies,
  },
  contactsID: {
    text: "View contact",
    permissionKey: "view",
    location: APP_ROUTES.contacts,
  },
  projectsID: {
    text: "View project",
    permissionKey: "view",
    location: APP_ROUTES.projects,
  },
  surveysID: {
    text: "View survey",
    permissionKey: "view",
    location: "/",
  },
};

/**
 * Configuration for "Add new" action links in invoice forms
 * Defines navigation routes and permissions for creating related entities
 *
 * TODO: Update config as new modules are added
 */
export const actionLinkTextConfig = {
  clientID: {
    text: "Add new client",
    permissionKey: "create",
    location: APP_ROUTES.sites,
  },
  contactsID: {
    text: "Add new contact",
    permissionKey: "create",
    location: APP_ROUTES.contacts,
  },
  projectsID: {
    text: "Add new project",
    permissionKey: "create",
    location: APP_ROUTES.projects,
  },
  surveysID: {
    text: "Add new survey",
    permissionKey: "create",
    location: "/",
  },
};

/**
 * Handler for "View" link clicks in search fields
 *
 * Navigates to the appropriate module view with filters applied
 * Sets localStorage keys for filter, action, and selected ID
 *
 * @param field - The field name that triggered the link click
 * @param option - The selected option containing value and label
 *
 * @example
 * // When user clicks "View project" link
 * onLinkClick("projectsID", { value: 123, label: "Project ABC" });
 * // Navigates to projects page with filter: Projects__Id EQ (123)
 *
 * TODO: Update config - Update cases for modules not built yet
 */
export const onLinkClick = <T>(field: keyof T, option: SelectOption) => {
  const navigate = useNavigate();
  let filterString = "";
  let route = "";

  let action: string | null = null;
  let actionSelectedId: string | null = null;

  // TODO - UpdateConfig - Update cases for modules not built yet
  switch (field) {
    case "projectName":
    case "projectsDescription":
    case "bActive":
      return;
    case "clientID":
      filterString = `Companies__Id EQ (${option.value}) | CompanyTypes__CompanyTypesName CONTAINS (Client)`;
      route = APP_ROUTES.companies;
      break;
    case "contactsID":
      filterString = `Contacts__Id EQ (${option.value})`;
      route = APP_ROUTES.contacts;

      action = "VIEW";
      actionSelectedId = `${option.value}`;
      break;
    case "projectsID":
      filterString = `Projects__Id EQ (${option.value})`;
      route = APP_ROUTES.projects;

      action = "VIEW";
      actionSelectedId = `${option.value}`;
      break;

    case "surveysID":
      filterString = `Surveys__Id EQ (${option.value})`;
      route = ""; // TODO: Add surveys
      break;
  }

  if (filterString) {
    window.localStorage.setItem(FILTER_KEY, filterString);
  }

  if (action) {
    window.localStorage.setItem(ACTION_KEY, action);
  }

  if (actionSelectedId) {
    window.localStorage.setItem(ACTION_SELECTED_ID_KEY, actionSelectedId);
  }

  if (route) {
    // window.open(route, "_blank", "noreferrer");
    navigate(route);
  }
};

/**
 * Handler for "Add new" action link clicks in search fields
 *
 * Navigates to the appropriate module create form
 * Sets localStorage keys for action type and selected ID
 *
 * @param field - The field name that triggered the action link click
 *
 * @example
 * // When user clicks "Add new contact" link
 * onActionLinkClick("contactsID");
 * // Navigates to contacts page with action: CREATE_BULK
 *
 * TODO: Update config - Update cases for modules not built yet
 */
export const onActionLinkClick = <T>(field: keyof T) => {
  const navigate = useNavigate();
  let actionSelectedId: string | null = null;
  let action = "";

  let route = "";

  console.log("onActionLinkClick: ", field);

  // TODO - UpdateConfig - Update cases for modules not built yet
  switch (field) {
    case "projectName":
    case "projectsDescription":
    case "bActive":
      return;
    case "sitesIds":
      route = APP_ROUTES.sites;
      action = "CREATE";
      break;
    case "parentProgrammeIds":
      route = "";
      action = "CREATE";
      break;
    case "employeesInTeamIds":
      route = "";
      action = "CREATE";
      break;
    case "clientCompanyIds":
      route = APP_ROUTES.companies;
      action = "CREATE";
      break;
    case "supplierCompaniesIds":
      route = APP_ROUTES.companies;
      action = "CREATE";
      break;
    case "assetsIds":
      route = "";
      action = "CREATE";
      break;
    case "workflowsIds":
      route = "";
      action = "CREATE";
      break;
    case "documentsIds":
      route = "";
      action = "CREATE";
      break;
    case "contactsID":
      // TODO: Take to bulk uploads panel on the contacts page, pass through the ID
      route = APP_ROUTES.contacts;

      action = "CREATE_BULK";

      break;

    case "clientId":
      route = APP_ROUTES.companies;
      action = "CREATE";
      break;
  }

  if (action) {
    window.localStorage.setItem(ACTION_KEY, action);
  }

  if (actionSelectedId) {
    window.localStorage.setItem(ACTION_SELECTED_ID_KEY, actionSelectedId);
  }

  if (route) {
    navigate(route);
  }
};

/**
 * Action types that include document update permissions
 * These action types allow users to upload/modify invoice documents
 */
export const actionTypesWithUpdateDocumentPermissions = ["update", "update-apclerk"];
